import { useGT } from 'gt-react';
import type { GetLeadTimeDetailResponse } from '@erp/rpc';
import type { LeadTimeCompositionSnapshot } from '@erp/shared-types';

export function CompositionRows({
  snapshot,
}: {
  snapshot: LeadTimeCompositionSnapshot;
}) {
  const gt = useGT();
  return (
    <div className="space-y-1 text-xs">
      {snapshot.components.map((component) => (
        <div key={component.id} className="flex justify-between gap-2">
          <span>
            {component.label}
            <span className="ml-1 text-gray-500">
              {component.source.kind === 'static'
                ? gt('Static', { context: 'lead time component source' })
                : gt('Calculated', {
                    context: 'lead time component source',
                  })}{' '}
              ·{' '}
              {component.owner === 'organization'
                ? gt('Organization', { context: 'lead time component owner' })
                : gt('SKU / warehouse override', {
                    context: 'lead time component owner',
                  })}
            </span>
            {component.quality === 'measured' && (
              <span className="ml-1 text-gray-500">
                {gt('Measured', {
                  context: 'lead time component evidence quality',
                })}
              </span>
            )}
            {component.quality === 'declared' && (
              <span className="ml-1 text-gray-500">
                {gt('Supplier declared', {
                  context: 'lead time component evidence quality',
                })}
              </span>
            )}
            {component.quality === 'assumed' && (
              <span className="ml-1 text-amber-700">
                {gt('Assumed', {
                  context: 'lead time missing history fallback',
                })}
              </span>
            )}
            {component.reason && (
              <div className="text-gray-500">{component.reason}</div>
            )}
          </span>
          <span className="tabular-nums">
            {component.days === null
              ? gt('Unavailable', { context: 'unresolved lead time component' })
              : gt('{days}d', {
                  context: 'lead time component calendar days',
                  days: component.days,
                })}
          </span>
        </div>
      ))}
      {snapshot.exclusionReason && (
        <p className="text-amber-700">
          {gt('Profile excluded: {reason}', {
            context: 'lead time eligibility exception',
            reason:
              snapshot.exclusionReason === 'transfer'
                ? gt('Transfer source', {
                    context: 'lead time component exclusion reason',
                  })
                : snapshot.exclusionReason === 'conflicting_source'
                  ? gt('Conflicting source settings', {
                      context: 'lead time component exclusion reason',
                    })
                  : gt('Vendor source not known', {
                      context: 'lead time component exclusion reason',
                    }),
          })}
        </p>
      )}
      {snapshot.status === 'total_override' && (
        <p>
          {gt(
            'The total lead time override supersedes these lead time components.',
            { context: 'lead time whole total pin precedence' },
          )}
        </p>
      )}
      <p className="text-gray-500">
        {gt('Revision {version} · {fingerprint}', {
          context: 'lead time saved settings revision',
          version: snapshot.profileVersion ?? 0,
          fingerprint: snapshot.settingsFingerprint.slice(0, 12),
        })}
      </p>
    </div>
  );
}

export function LeadTimeBreakdown({
  detail,
}: {
  detail: GetLeadTimeDetailResponse;
}) {
  const gt = useGT();
  if (!detail.currentComposition) return null;
  return (
    <div className="space-y-2 border-t pt-2">
      <p className="text-xs font-medium">
        {gt('Current lead time: {days} calendar days', {
          context:
            'lead time current settings preview distinct from saved policy',
          days: detail.currentComposition.effectiveDays ?? detail.fallbackDays,
        })}
      </p>
      <CompositionRows snapshot={detail.currentComposition} />
      {detail.changedSincePolicyRun && (
        <p role="status" className="text-xs text-amber-700">
          {gt(
            'Settings changed since the policy run. Existing quantities use the saved calculation. Run demand forecasting, then replenishment, to refresh them.',
            { context: 'lead time settings stale policy warning' },
          )}
        </p>
      )}
      {detail.policyComposition && (
        <details className="text-xs">
          <summary>
            {gt('Saved policy lead time: {days} calendar days', {
              context: 'lead time snapshot used for saved policy',
              days:
                detail.policyComposition.effectiveDays ?? detail.fallbackDays,
            })}
          </summary>
          <CompositionRows snapshot={detail.policyComposition} />
        </details>
      )}
    </div>
  );
}
